import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { PrimeIcons } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { SelectButtonModule } from 'primeng/selectbutton';

import { CurrentSiteState } from '@ppg/configuration';
import { ResourceItem } from '@ppg/core/enums';
import { Color, ColorInfoInvalid, ColorInfoValid } from '@ppg/core/models';
import { FeatureRoutePathService } from '@ppg/core/navigation';
import { BackgroundDirective, FontColorBasedOnBackgroundDirective } from '@ppg/shared/components';
import { ResourceValuePipe } from '@ppg/shared/resource';

@Component({
  selector: 'ppg-color-chip',
  standalone: true,
  imports: [
    NgClass,
    BackgroundDirective,
    CardModule,
    ButtonModule,
    SelectButtonModule,
    FormsModule,
    ResourceValuePipe,
    RouterLink,
    FontColorBasedOnBackgroundDirective,
  ],
  templateUrl: './color-chip.component.html',
  styleUrl: './color-chip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ColorChipComponent {
  readonly #featureRoutePathService = inject(FeatureRoutePathService);

  readonly color = input.required<Color>();
  readonly styleClass = input<string>('');

  readonly favoriteButtonClicked = output<Color>();
  readonly colorDetailsLinkClicked = output<Color>();

  readonly colorValid = computed(() => this.color() as ColorInfoValid);
  readonly colorInvalid = computed(() => this.color() as ColorInfoInvalid);
  readonly isVisualizable = computed(() => !!(this.colorValid().image || this.colorValid().rgb));
  readonly colorDetailsLink = computed(() => this.#featureRoutePathService.getColorDetailsPath(this.color()));

  readonly defaultImage = 'assets/img/image-outline.svg';
  readonly resources = {
    colorNotAvailable: ResourceItem.COLORS_FAVORITES_COLOR_IMAGE_UNAVAILABLE,
    colorNumberNotAvailable: ResourceItem.COLORS_FAVORITES_COLOR_NOT_AVAILABLE,
  };
  protected readonly PrimeIcons = PrimeIcons;

  onFavoriteButtonClicked(): void {
    this.favoriteButtonClicked.emit(this.color());
  }

  onColorDetailsLinkClicked(): void {
    this.colorDetailsLinkClicked.emit(this.color());
  }

  /* istanbul ignore next */
  // Temporary implementation: This will be removed or changed once the design team provides the appropriate styles.
  private readonly currentSiteState = inject(CurrentSiteState);
  readonly themeName = computed(() => this.currentSiteState.themeName());
  readonly isAlcro = computed(() => this.themeName() === 'alcro-light');
  readonly isHistor = computed(() => this.themeName() === 'histor-light');
  readonly isPPG = computed(() => this.themeName() === 'ppg-light');
}
